import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCached, requestTranslation, subscribe } from '@/lib/autoTranslate';

/**
 * TRADUZIONE GLOBALE DELL'INTERFACCIA.
 * Quando la lingua attiva != 'it', percorre il DOM e traduce i nodi di testo e gli
 * attributi visibili (placeholder/title/aria-label/alt) riusando l'infrastruttura AI
 * + cache di autoTranslate (endpoint /api/i18n/translate, cache RAM+localStorage+DB).
 *
 * Vantaggi: copre TUTTE le aree (cliente, CRM, pubblico) senza rifattorizzare i file.
 * Le stringhe già tradotte via i18n restano invariate; i numeri/prezzi/email/codici
 * vengono ignorati. Per escludere un elemento: attributo data-no-translate o translate="no".
 */

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'SVG', 'PATH', 'CANVAS', 'IFRAME']);
const SKIP_CLOSEST = '[data-no-translate],[translate="no"],.notranslate,input,textarea,[contenteditable="true"]';
const HAS_LETTER = /[A-Za-zÀ-ÿ]/;

// stato per nodo: { src: testo IT originale, out: ultima traduzione applicata }
const nodeState = new WeakMap();
const attrState = new WeakMap(); // element -> { [attr]: { src, out } }

function skippable(text) {
  const t = (text || '').trim();
  if (t.length < 2) return true;
  if (!HAS_LETTER.test(t)) return true;                 // solo numeri/simboli
  if (/^[€$£%\d.,:/\-+\s()]+$/.test(t)) return true;     // prezzi/numeri/codici numerici
  if (/@/.test(t) && /\.[a-z]{2,}/i.test(t)) return true; // email
  if (/^https?:\/\//i.test(t)) return true;             // url
  if (/^SILA[-\s]/i.test(t)) return true;               // codici tessera
  return false;
}

function splitWs(s) {
  const m = s.match(/^(\s*)([\s\S]*?)(\s*)$/);
  return { lead: m[1], core: m[2], trail: m[3] };
}

function textNodes() {
  const out = [];
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentNode;
      if (!p || SKIP_TAGS.has(p.nodeName)) return NodeFilter.FILTER_REJECT;
      if (p.closest && p.closest(SKIP_CLOSEST)) return NodeFilter.FILTER_REJECT;
      const v = node.nodeValue;
      if (!v || skippable(v)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  // eslint-disable-next-line no-cond-assign
  while ((n = walker.nextNode())) out.push(n);
  return out;
}

function translateTextNodes(lang) {
  const nodes = textNodes();
  for (const node of nodes) {
    const cur = node.nodeValue;
    let st = nodeState.get(node);
    // rileva contenuto nuovo/cambiato da React
    if (!st || (cur !== st.out && cur !== st.src)) {
      st = { src: cur, out: undefined };
      nodeState.set(node, st);
    }
    if (lang === 'it') {
      if (cur !== st.src) node.nodeValue = st.src;
      st.out = st.src;
      continue;
    }
    const { lead, core, trail } = splitWs(st.src);
    if (!core || skippable(core)) continue;
    const tr = getCached(lang, core);
    if (tr && tr !== core) {
      const next = lead + tr + trail;
      st.out = next;
      if (cur !== next) node.nodeValue = next;
    } else {
      requestTranslation(lang, core);
    }
  }
}

const ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];

function translateAttrs(lang) {
  const els = document.body.querySelectorAll('[placeholder],[title],[aria-label],img[alt]');
  els.forEach((el) => {
    if (el.closest && el.closest('[data-no-translate],[translate="no"],.notranslate')) return;
    let store = attrState.get(el);
    if (!store) { store = {}; attrState.set(el, store); }
    for (const attr of ATTRS) {
      if (!el.hasAttribute(attr)) continue;
      const cur = el.getAttribute(attr);
      let s = store[attr];
      if (!s || (cur !== s.out && cur !== s.src)) { s = { src: cur, out: undefined }; store[attr] = s; }
      if (lang === 'it') { if (cur !== s.src) el.setAttribute(attr, s.src); s.out = s.src; continue; }
      const core = (s.src || '').trim();
      if (!core || skippable(core)) continue;
      const tr = getCached(lang, core);
      if (tr && tr !== core) { s.out = tr; if (cur !== tr) el.setAttribute(attr, tr); }
      else requestTranslation(lang, core);
    }
  });
}

export default function GlobalAutoTranslate() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'it';

  useEffect(() => {
    let raf = null;
    const run = () => {
      raf = null;
      try { translateTextNodes(lang); translateAttrs(lang); } catch { /* noop */ }
    };
    const schedule = () => { if (raf) return; raf = window.requestAnimationFrame(run); };

    // giro iniziale (anche per 'it' => ripristina eventuali traduzioni precedenti)
    schedule();

    if (lang === 'it') {
      return () => { if (raf) cancelAnimationFrame(raf); };
    }

    const unsub = subscribe(schedule);           // riapplica quando arrivano traduzioni AI
    const obs = new MutationObserver(schedule);  // contenuti che cambiano (SPA)
    obs.observe(document.body, { childList: true, characterData: true, subtree: true });
    const iv = window.setInterval(schedule, 2000); // rete di sicurezza per contenuti async

    return () => {
      unsub();
      obs.disconnect();
      window.clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [lang]);

  return null;
}
